/**
 * Whether a row belongs in a view.
 *
 * A view's filters form a tree: filters sit in groups, groups nest inside other
 * groups, and each node combines its children with `AND` or `OR`. This answers
 * the tree for one row, so an edited row can be re-checked without a round trip.
 *
 * Pure, like the filter types it calls: plain data in, a boolean out.
 */
import { VIEW_FILTER_TYPES } from '../filters'
import type { FilterContext } from '../filters'
import type { Field, FieldKey } from '@/api/types'

/** How a node combines the answers beneath it. */
export type FilterConjunction = 'AND' | 'OR'

/**
 * The cells a filter tree reads: `field_<id>` keys and nothing else.
 *
 * A stored row has these, and so does a half-filled form -- which has no id and
 * no order and never will.
 */
export type FilterableCells = { [key: FieldKey]: unknown }

/**
 * The parts of a stored condition this module reads.
 *
 * Structural rather than `ViewFilter`, because the same tree is asked of two
 * different owners: a view's filters decide which rows it holds, and a form
 * question's conditions decide whether it is asked at all. Neither the view id
 * nor the preloaded display name means anything here.
 */
export interface FilterableCondition {
  /**
   * The column compared, or null for one whose column has gone.
   *
   * A view's filters are deleted with their column. A colour's are not always:
   * the conditional provider drops a condition naming a deleted column, but its
   * serializer allows `null` on both this and the type, so a decoration written
   * by another version -- or one caught mid-change -- can hold either. Neither
   * matches any field, so such a condition is skipped, exactly as a filter type
   * this build does not know is.
   */
  field: number | null
  type: string | null
  value?: string
  /**
   * The group it nests in.
   *
   * A number for a view's filters, which are rows; a string for a colour's
   * conditions, which live inside one JSON column and are named by whoever
   * wrote them. The tree only ever compares these for equality, so it does not
   * care which -- and saying so here is what lets one matcher serve both.
   */
  group?: number | string | null
}

/**
 * The parts of a group this module reads.
 *
 * Structural for the same reason the condition is: a view's groups are rows and
 * a colour's are entries in a JSON column, and the tree only ever compares
 * their ids for equality.
 */
export interface FilterableGroup {
  id: number | string
  filter_type?: string
  parent_group?: number | string | null
}

/** The parts of a view -- or of a form question -- this module reads. */
export interface FilterableView {
  filters_disabled?: boolean
  filter_type?: string
  filters?: readonly FilterableCondition[]
  filter_groups?: readonly FilterableGroup[]
}

/**
 * One node of the tree: a group, or the root standing in for the view itself.
 *
 * Generic over the condition, because the same tree is built for two different
 * readers. Evaluating a row needs only `field op value`; *editing* the tree
 * needs the stored filters themselves, ids and all -- and building it twice
 * would be two places deciding what happens to a filter whose group is missing.
 */
export interface TreeNode<T extends FilterableCondition = FilterableCondition> {
  /** The group this node is, or null for the root. */
  id: number | string | null
  conjunction: FilterConjunction
  filters: T[]
  children: TreeNode<T>[]
}

const conjunctionOf = (value: string | undefined): FilterConjunction =>
  value === 'OR' ? 'OR' : 'AND'

/**
 * Builds the filter tree for a view.
 *
 * Every node is made before any is attached, so nothing depends on the order
 * the groups arrive in. The first version attached in one pass over the groups
 * sorted by id, relying on a parent always having the smaller one -- true of a
 * view's groups, which are rows the database numbers. A colour's are named by
 * whoever wrote them, and subtracting one name from another gives `NaN`, which
 * `sort` reads as "equal": the sort quietly becomes a no-op and a group listed
 * before its parent goes to the root instead, reappearing at the top level as
 * conditions nobody wrote.
 *
 * A filter or group whose parent is missing is attached to the root rather than
 * dropped: a condition nobody can see is worse than one in the wrong place.
 */
export function buildFilterTree<T extends FilterableCondition>(view: {
  filter_type?: string
  filters?: readonly T[]
  filter_groups?: readonly FilterableGroup[]
}): TreeNode<T> {
  const root: TreeNode<T> = {
    id: null,
    conjunction: conjunctionOf(view.filter_type),
    filters: [],
    children: [],
  }
  const groups = view.filter_groups ?? []
  const byId = new Map<number | string, TreeNode<T>>()

  for (const group of groups) {
    byId.set(group.id, {
      id: group.id,
      conjunction: conjunctionOf(group.filter_type),
      filters: [],
      children: [],
    })
  }

  for (const group of groups) {
    const node = byId.get(group.id)
    if (node === undefined) continue
    const parent =
      group.parent_group == null ? root : (byId.get(group.parent_group) ?? root)
    parent.children.push(node)
  }

  for (const filter of view.filters ?? []) {
    const node = filter.group == null ? root : (byId.get(filter.group) ?? root)
    node.filters.push(filter)
  }

  return root
}

/**
 * A group and everything nested inside it, however deep.
 *
 * What removing a group takes with it: the backend deletes a group's children
 * and their conditions, and leaving either behind would leave conditions whose
 * group is missing -- which `buildFilterTree` attaches to the *root*, so they
 * would reappear at the top level as conditions nobody wrote.
 *
 * Walked from the parent down rather than swept in id order. A view's groups
 * are rows whose parent always has the smaller id, so one ordered pass reaches
 * a child after the parent that condemned it; a colour's are named rather than
 * numbered and have no such order to sweep in.
 */
export function groupsWithin(
  groups: readonly FilterableGroup[],
  groupId: number | string,
): Set<number | string> {
  const condemned = new Set<number | string>([groupId])

  // Repeated until nothing more is reached, because a child may be listed
  // before its parent and one pass would then miss it.
  let reached = true
  while (reached) {
    reached = false
    for (const group of groups) {
      if (condemned.has(group.id)) continue
      if (group.parent_group != null && condemned.has(group.parent_group)) {
        condemned.add(group.id)
        reached = true
      }
    }
  }

  return condemned
}

/** The answer a node gives, and whether anything under it had an opinion. */
interface NodeResult {
  matches: boolean
  hasOpinion: boolean
}

function evaluate(
  node: TreeNode,
  fields: readonly Field[],
  row: FilterableCells,
  context?: FilterContext,
): NodeResult {
  let hasOpinion = false

  for (const child of node.children) {
    const result = evaluate(child, fields, row, context)
    if (result.hasOpinion) hasOpinion = true

    if (node.conjunction === 'AND' && !result.matches) {
      return { matches: false, hasOpinion }
    }
    if (node.conjunction === 'OR' && result.matches) {
      return { matches: true, hasOpinion }
    }
  }

  for (const filter of node.filters) {
    const field = fields.find((f) => f.id === filter.field)
    if (field === undefined) continue

    const definition = VIEW_FILTER_TYPES[filter.type as keyof typeof VIEW_FILTER_TYPES]
    // A filter type the frontend does not know is skipped rather than treated
    // as a failure: the server is the authority, and hiding rows on the basis
    // of a filter this build cannot evaluate would be worse than showing them.
    if (definition === undefined) continue

    const result = definition.matches(
      row[`field_${field.id}`],
      String(filter.value ?? ''),
      field,
      context,
    )

    // `null` means the filter has no opinion -- an empty value, most often --
    // so it neither matches nor fails, and does not count towards whether the
    // node had any opinion at all.
    if (result === null) continue
    hasOpinion = true

    if (node.conjunction === 'AND' && !result) return { matches: false, hasOpinion }
    if (node.conjunction === 'OR' && result) return { matches: true, hasOpinion }
  }

  if (node.conjunction === 'AND') return { matches: true, hasOpinion }

  // Nothing under an `OR` matched. If nothing had an opinion either, there was
  // no filtering to apply and the row stays; if something did and none of it
  // matched, the row is out.
  return { matches: !hasOpinion, hasOpinion }
}

/**
 * Whether a row passes a view's filters.
 *
 * A view with filters switched off, or with none at all, keeps every row.
 */
export function rowMatchesFilters(
  row: FilterableCells,
  view: FilterableView,
  fields: readonly Field[],
  context?: FilterContext,
): boolean {
  if (view.filters_disabled === true) return true
  if ((view.filters ?? []).length === 0) return true
  return evaluate(buildFilterTree(view), fields, row, context).matches
}
